// ============================================================
// THE SOLID UNDER THE POINTER, whichever renderer draws the terrain (perf overhaul, Stage 2b).
//
// The svg map's faces carry `data-solid-id`, so a press reads the face the browser hit. While the GPU canvas draws
// the terrain (`data-gpu` on the svg) there are no faces in the DOM: the pointer's line of sight is cast into the
// solids instead (engine/display/gpu/pick), the nearest one entered - what the topmost face would have been. A press on
// a figure is the figure's, not the wall's behind it, as it is on the svg. Hidden terrain is pickable only by those
// who see it drawn (the GM).
// ============================================================
import { SCENE_VIEW } from '../engine/battlefield/projection';
import type { Camera } from '../engine/battlefield/projection';
import type { Solid } from '../engine/battlefield/types';
import { pickSolid } from '../engine/display/gpu/pick';
import { pointerToScene } from './scenePointer';
import type { ScenePointerEvent } from './scenePointer';

interface Picked {
  getAttribute?: (name: string) => string | null;
  closest?: (selector: string) => unknown;
}

export interface SolidPointerEvent extends ScenePointerEvent {
  target: Picked | null;
}

interface Scene {
  solids: readonly Solid[];
  camera: Camera;
  /** Hidden terrain is drawn, so pickable (the GM). */
  includeHidden: boolean;
}

/**
 * Is the GPU canvas drawing this event's scene? The svg says so (`data-gpu`): it is the one published statement of
 * who draws the terrain - DisplayScene sets it, and drops it again if WebGL fails - and every caller has the svg's
 * event, the gestures and the deploy hook alike.
 */
function drawnOnGpu(e: SolidPointerEvent): boolean {
  const svg = (e.currentTarget.ownerSVGElement || e.currentTarget) as { getAttribute?: (n: string) => string | null };
  return svg.getAttribute?.('data-gpu') === 'true';
}

/** The solid under the pointer, or null: its face on the svg, its line of sight on the GPU map. */
export function solidUnderPointer(e: SolidPointerEvent, { solids, camera, includeHidden }: Scene): Solid | null {
  if (!drawnOnGpu(e)) {
    const id = e.target?.getAttribute?.('data-solid-id');
    return id ? (solids.find((s) => s.id === id) ?? null) : null;
  }
  if (e.target?.closest?.('[data-token-id]')) return null;
  const p = pointerToScene(e);
  return pickSolid(p.x, p.y, solids, camera, SCENE_VIEW, includeHidden);
}
